'use client';

import CheckConfirm from '@/assets/icon/check-confirm';
import { useRouter, useSearchParams } from '@/lib/navigation';
import Link from '@/lib/app-link';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CheckPending from '@/assets/icon/checkpending';
import Failed from '@/assets/icon/failed';
import {
  clearLocalState,
  updateReqData,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch } from 'react-redux';
import {
  useViewBookingMutation,
  useIntiateBookingMutation,
  useConfirmBookingMutation,
} from '@flysync/shared-redux/services/booking';
import { config } from '@/config';
import { useToast } from '@/hooks/use-toast';

const Confirm = () => {
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const [viewBooking, { data: finalRes, isLoading }] = useViewBookingMutation();
  const [initiateBooking, { data: initiateBookingRes }] =
    useIntiateBookingMutation();
  const [
    confirmBooking,
    { data: confirmBookingRes, isLoading: confirmBookingLoading },
  ] = useConfirmBookingMutation();
  // Ticket page will be redirected funtion
  const router = useRouter();
  const dispatch = useDispatch();
  const flightSearch = useSelector((state: any) => state.flightSearch);
  const userInput = useSelector((state: any) => state.userInputs);
  const { selectedFlight, selectedBucket, selectedReturnFlight } = userInput;

  const [isLoader, setIsLoader] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasRun = useRef(false);
  const flightSearchRef = useRef(flightSearch);

  useEffect(() => {
    flightSearchRef.current = flightSearch;
  }, [flightSearch]);

  useEffect(() => {
    if (hasRun.current) return;
    
    // Ensure we're on the client side
    if (typeof window === 'undefined') {
      return;
    }
    
    sessionStorage?.removeItem('seatData');
    sessionStorage?.removeItem('ssrInfoData');
    
    const viewBookingCall = async () => {
      try {
        const wait = (ms: number) =>
          new Promise((resolve) => setTimeout(resolve, ms));

        // Payment gateway callbacks can reach this page before redux is fully hydrated.
        // Wait briefly for required booking data instead of failing immediately.
        const getStableFlightSearch = async () => {
          for (let attempt = 0; attempt < 10; attempt++) {
            const current = flightSearchRef.current;
            const hasInitReqData = Boolean(current?.initiateReqData);
            const hasPassengerData = Boolean(
              current?.passenger && current.passenger.length > 0,
            );

            if (hasInitReqData && hasPassengerData) {
              return current;
            }

            await wait(300);
          }

          return flightSearchRef.current;
        };

        const urlParams = new URLSearchParams(window.location.search);
        const orderId = searchParams?.get('orderId') || urlParams.get('orderId');
        const paymentStatus = (
          searchParams?.get('status') ||
          urlParams.get('status') ||
          ''
        ).toLowerCase();
        const paymentMessage =
          searchParams?.get('message') || urlParams.get('message') || '';

        // Validate orderId from payment gateway
        if (!orderId) {
          setError('Payment order ID is missing. Please contact support.');
          setIsLoader(false);
          toast({
            title: 'Error',
            description: 'Payment order ID is missing. Please try again or contact support.',
            variant: 'error',
          });
          return;
        }

        if (paymentStatus === 'failed') {
          setError(paymentMessage || 'Payment failed. Please try again.');
          setIsLoader(false);
          toast({
            title: 'Payment failed',
            description: paymentMessage || 'Payment failed. Please try again.',
            variant: 'error',
          });
          return;
        }

        if (paymentStatus === 'pending') {
          dispatch(
            updateReqData({
              ...(flightSearchRef.current || {}),
              booking_id: orderId,
              bookingRefId: orderId,
              statusCode: 'PN',
              bookingStatus: 'Pending',
            }),
          );
          setIsLoader(false);
          return;
        }

        if (paymentStatus === 'success') {
          try {
            const existingBookingRes = await viewBooking({
              data: { booking_id: orderId },
              auth: sessionStorage.getItem('token') || '',
            }).unwrap();
            const existingBookingData = existingBookingRes?.data?.data
              ? existingBookingRes.data.data
              : existingBookingRes?.data;
            const bookingRefId =
              existingBookingData?.bookingDetails?.[0]?.booking_id;

            if (bookingRefId) {
              const statusCode = existingBookingData?.statusCode || '';
              dispatch(
                updateReqData({
                  ...(flightSearchRef.current || {}),
                  booking_id: orderId,
                  bookingRefId,
                  statusCode,
                  bookingStatus: existingBookingData?.status || '',
                }),
              );
              setIsLoader(false);

              if (statusCode === 'CNF') {
                setTimeout(() => {
                  router.push(
                    '/ticket?booking_id=' +
                      encodeURIComponent(String(orderId)),
                  );
                }, 5000);
              }
              return;
            }
          } catch (viewError) {
            console.warn('Booking view fallback after PG success:', viewError);
          }
        }

        // Validate required data exists
        const stableFlightSearch = await getStableFlightSearch();

        if (!stableFlightSearch?.initiateReqData) {
          setError('Booking data not found. Please try again.');
          setIsLoader(false);
          toast({
            title: 'Error',
            description: 'Booking data not found. Please try again.',
            variant: 'error',
          });
          return;
        }

        // Validate passenger data
        if (
          !stableFlightSearch.passenger ||
          stableFlightSearch.passenger.length === 0
        ) {
          setError('Passenger information not found. Please try again.');
          setIsLoader(false);
          toast({
            title: 'Error',
            description: 'Passenger information not found. Please try again.',
            variant: 'error',
          });
          return;
        }

        let reqData = {
          ...stableFlightSearch.initiateReqData,
          paymentId: orderId,
        };
        const initRes = await initiateBooking({
          data: reqData,
          auth: sessionStorage.getItem('token') || '',
        }).unwrap();

        if (!initRes?.data) {
          setError('Failed to initiate booking. Please try again.');
          setIsLoader(false);
          toast({
            title: 'Booking failed',
            description: 'Failed to initiate booking. Please try again.',
            variant: 'error',
          });
          return;
        }

        reqData.booking_id = initRes?.data || '';
        const confirmBookingRes = await confirmBooking({
          data: reqData,
          auth: sessionStorage.getItem('token') || '',
        }).unwrap();

        if (confirmBookingRes?.status.code !== 200) {
          setError('Booking confirmation failed. Please contact support.');
          setIsLoader(false);
          toast({
            title: 'Booking failed',
            description: 'Booking confirmation failed. Please contact support.',
            variant: 'error',
          });
          return;
        }

        const finalRes = await viewBooking({
          data: { booking_id: reqData.booking_id },
          auth: sessionStorage.getItem('token') || '',
        }).unwrap();

        const responseData = finalRes?.data?.data
          ? finalRes?.data?.data
          : finalRes?.data;

        if (!responseData?.bookingDetails?.[0]?.booking_id) {
          setError('Booking details not found. Please contact support.');
          setIsLoader(false);
          toast({
            title: 'Error',
            description: 'Booking details not found. Please contact support.',
            variant: 'error',
          });
          return;
        }

        const bookingRefId = responseData?.bookingDetails[0]?.booking_id;
        const statusCode = responseData?.statusCode;
        const bookingStatus = responseData?.status;
        const stateData = {
          ...stableFlightSearch,
          booking_id: reqData?.booking_id,
          bookingRefId: bookingRefId || '',
          statusCode: statusCode || '',
          bookingStatus: bookingStatus || '',
        };
        dispatch(updateReqData(stateData));
        
        if (statusCode === 'PN') {
          clearLocalState(selectedFlight);
          clearLocalState(selectedBucket);
          clearLocalState(selectedReturnFlight);
        }
        
        setIsLoader(false);
        
        if (statusCode === 'CNF') {
          const ticketBookingId = reqData?.booking_id;
          const ticketUrl = ticketBookingId
            ? `/ticket?booking_id=${encodeURIComponent(String(ticketBookingId))}`
            : '/ticket';

          const timer = setTimeout(() => {
            router.push(ticketUrl);
          }, 5000);
          return () => clearTimeout(timer);
        }
      } catch (error: any) {
        console.error('Booking confirmation error:', error);
        setError(error?.message || 'An error occurred during booking confirmation.');
        setIsLoader(false);
        toast({
          title: 'Error',
          description: error?.message || 'An error occurred. Please try again.',
          variant: 'error',
        });
        // Don't redirect immediately on error, let user see the error message
      }
    };
    
    viewBookingCall();
    hasRun.current = true;
  }, [viewBooking, dispatch, flightSearch, router, initiateBooking, confirmBooking, selectedFlight, selectedBucket, selectedReturnFlight, searchParams]);

  // useEffect(() => {
  //   const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  //     e.preventDefault();
  //     e.returnValue = '';
  //     dispatch(clearLocalState());
  //   };

  //   const handlePopState = () => {
  //     if (flightSearch.statusCode !== 'CNF') {
  //       window.history.pushState(null, '', window.location.pathname);
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);
  //   window.addEventListener('popstate', handlePopState);

  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload);
  //     window.removeEventListener('popstate', handlePopState);
  //   };
  // }, [flightSearch.statusCode]);

  return (
    <>
      {isLoader ? (
        <div className="flex h-full items-center justify-center">
          <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-tertiary">
          <div className="h-14 w-full bg-primary sm:h-20 lg:h-48" />
          <div className="absolute inset-x-0 top-14 mx-auto px-1 pb-4 sm:top-14 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
            <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
              <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
                Booking Confirmation
              </h2>
            </div>
            <div className="relative rounded-xl bg-white shadow-lg">
              <div className="flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
                <Failed />
                <p className="mb-3 text-xl font-semibold text-red-500 sm:mb-4 sm:text-2xl lg:text-3xl">
                  Booking Failed
                </p>
                <div className="mt-3 text-center sm:mt-4">
                  <p className="px-4 text-base font-medium text-red-600 sm:px-6 sm:text-xl lg:text-2xl">
                    {error}
                  </p>
                </div>
                <div
                  className="mb-3 mt-6 cursor-pointer text-sm text-blue-600 hover:underline sm:mb-4 sm:mt-8 sm:text-base lg:mt-10"
                  onClick={() => router.push('/')}
                >
                  Back to Home
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-tertiary">
          <div className="h-14 w-full bg-primary sm:h-20 lg:h-48" />
          <div className="absolute inset-x-0 top-14 mx-auto px-1 pb-4 sm:top-14 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
            <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
              <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
                Booking Confirmation
              </h2>
            </div>
            <div className="relative rounded-xl bg-white shadow-lg">
              {flightSearch.statusCode === 'CNF' && (
                <p className="absolute right-3 top-3 text-xs text-gray-600 sm:right-4 sm:top-4 sm:text-sm">
                  This screen will be redirected to the ticket page.
                </p>
              )}
              <div className="flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
                {/* Check Icon */}
                <div>
                  {flightSearch.statusCode === 'PN' ? (
                    <CheckPending />
                  ) : flightSearch.statusCode === 'CNF' ? (
                    <CheckConfirm />
                  ) : (
                    <Failed />
                  )}
                </div>
                <p
                  className={`mb-3 text-xl font-semibold sm:mb-4 sm:text-2xl lg:text-3xl ${
                    flightSearch.statusCode === 'PN'
                      ? 'text-orange-400'
                      : flightSearch.statusCode === 'CNF'
                        ? 'text-green-500'
                        : 'text-red-500'
                  }`}
                >
                  {flightSearch.statusCode === 'PN'
                    ? 'Booking Pending'
                    : flightSearch.statusCode === 'CNF'
                      ? 'Booking Successful'
                      : 'Booking Failed'}
                </p>
                <div className="mt-3 text-center sm:mt-4">
                  <p className="px-4 text-base font-medium sm:px-6 sm:text-xl lg:text-2xl">
                    {flightSearch.statusCode === 'PN'
                      ? 'Kindly contact our customer care for more details.'
                      : flightSearch.statusCode === 'CNF'
                        ? 'Your tickets will be sent to your Mobile number and Email ID'
                        : 'Payment failed. Please try again '}
                  </p>
                  {flightSearch.statusCode === 'PN' && (
                    <div className="mt-2 text-sm text-gray-600 sm:mt-3 sm:text-base lg:text-lg">
                      <p className="mb-1">
                        Mobile:{' '}
                        <span className="font-semibold">{config.phone}</span>
                      </p>
                      <p>
                        Email:{' '}
                        <span className="break-all font-semibold">
                          {config.email}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
                {flightSearch.statusCode === 'CNF' && (
                  <p className="mt-3 text-base font-semibold sm:mt-4 sm:text-xl lg:text-2xl">
                    Booking ID : {flightSearch.bookingRefId}
                  </p>
                )}
                <div
                  className="mb-3 mt-6 cursor-pointer text-sm text-blue-600 hover:underline sm:mb-4 sm:mt-8 sm:text-base lg:mt-10"
                  onClick={() => router.push('/')}
                >
                  Back to Home
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Confirm;
